import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { User, AuthState, LoginCredentials, RegisterData, AuthResponse } from '../types/auth';
import { backendErrorCode, isAxiosError } from '../types/error';
import { authService, tokenManager } from '../services/authService';
import { useTranslation, translateBackendError } from '../i18n';
import { logger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle: (idToken: string, confirmLink?: boolean) => Promise<{ requiresLinkConfirmation: boolean }>;
  register: (userData: RegisterData) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  /** Take a session another flow already got (the first-goal flow's code or Google sign-in,
   * T-158) without the global loading screen in between. */
  signIn: (authData: AuthResponse) => void;
  clearPendingVerification: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'VERIFICATION_REQUIRED'; payload: string }
  | { type: 'CLEAR_PENDING_VERIFICATION' };

// Initial state - check for existing token
const getInitialState = (): AuthState => {
  const existingToken = tokenManager.getToken();
  
  return {
    user: null,
    token: existingToken,
    isAuthenticated: !!existingToken, // If token exists, assume authenticated initially
    isLoading: true, // Still need to verify token validity
    error: null,
    pendingVerificationEmail: null,
  };
};

const initialState: AuthState = getInitialState();

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        pendingVerificationEmail: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'VERIFICATION_REQUIRED':
      return {
        ...state,
        isLoading: false,
        error: null,
        pendingVerificationEmail: action.payload,
      };
    case 'CLEAR_PENDING_VERIFICATION':
      return {
        ...state,
        pendingVerificationEmail: null,
      };
    default:
      return state;
  }
};

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { t } = useTranslation();
  const userRef = useRef(state.user);

  useEffect(() => {
    userRef.current = state.user;
  }, [state.user]);

  const checkAuth = useCallback(async () => {
    try {
      // Don't show loading if we already have a token and user
      const hasToken = tokenManager.getToken();
      if (!hasToken || !userRef.current) {
        dispatch({ type: 'SET_LOADING', payload: true });
      }
      
      if (authService.isAuthenticated()) {
        // If we already have user data, skip the API call
        if (userRef.current && hasToken) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        
        const user = await authService.getCurrentUser();
        const token = tokenManager.getToken();
        
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, token: token || '' },
        });
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    } catch (error) {
      logger.error('Auth check failed', error);
      dispatch({ type: 'AUTH_LOGOUT' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Check authentication status on app load
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const authData = await authService.login(credentials);
      trackEvent('login', { method: 'password' });
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authData.user,
          token: authData.access_token,
        },
      });
    } catch (error: unknown) {
      // Right password, unverified email: show the "check your inbox" screen instead of an error
      if (isAxiosError(error) && backendErrorCode(error.response?.data) === 'EMAIL_NOT_VERIFIED') {
        dispatch({ type: 'VERIFICATION_REQUIRED', payload: error.response?.data?.details?.email || credentials.username });
        return;
      }
      // Translate backend error message
      const errorMessage = translateBackendError(
        t,
        isAxiosError(error) ? error.response?.data : undefined,
        t('auth.backendErrors.LOGIN_FAILED')
      );
      
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const loginWithGoogle = async (idToken: string, confirmLink = false) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const authData = await authService.loginWithGoogle({ idToken, confirmLink });
      trackEvent(authData.isNewUser ? 'sign_up' : 'login', { method: 'google' });

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: authData.user,
          token: authData.access_token,
        },
      });

      return { requiresLinkConfirmation: false };
    } catch (error: unknown) {
      if (isAxiosError(error) && backendErrorCode(error.response?.data) === 'LINK_CONFIRMATION_REQUIRED') {
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'CLEAR_ERROR' });
        return { requiresLinkConfirmation: true };
      }

      const errorMessage = translateBackendError(
        t,
        isAxiosError(error) ? error.response?.data : undefined,
        t('auth.backendErrors.LOGIN_FAILED')
      );

      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const result = await authService.register(userData);
      trackEvent('sign_up', { method: 'password' });
      dispatch({ type: 'VERIFICATION_REQUIRED', payload: result.email });
    } catch (error: unknown) {
      // Translate backend error message
      const errorMessage = translateBackendError(
        t,
        isAxiosError(error) ? error.response?.data : undefined,
        t('auth.backendErrors.REGISTER_FAILED')
      );
      
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const verifyEmail = async (token: string) => {
    const authData = await authService.verifyEmail(token);
    trackEvent('email_verified', {});
    dispatch({
      type: 'AUTH_SUCCESS',
      payload: {
        user: authData.user,
        token: authData.access_token,
      },
    });
  };

  const signIn = useCallback((authData: AuthResponse) => {
    dispatch({ type: 'AUTH_SUCCESS', payload: { user: authData.user, token: authData.access_token } });
  }, []);

  const clearPendingVerification = useCallback(() => {
    dispatch({ type: 'CLEAR_PENDING_VERIFICATION' });
  }, []);

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      logger.error('Logout error', error);
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
      // Domain'in başlangıç adresine yönlendir
      window.location.href = window.location.origin;
    }
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const updateUser = useCallback((user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  }, []);

  const value: AuthContextType = {
    state,
    login,
    loginWithGoogle,
    register,
    verifyEmail,
    signIn,
    clearPendingVerification,
    logout,
    checkAuth,
    clearError,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
